import { useEffect } from 'react';
import { useDeps } from '../app/deps';

/**
 * Captures waiting on the phone go when they can (spec 3A §6.4): on load,
 * on reconnect, when the app comes back to the front, and every 30 seconds.
 * A reconnect sends at once, whatever the backoff said: the backoff is for
 * a server that's struggling, not for a phone that was offline. When any
 * went, the books on screen refresh.
 */
export function useOutboxSync(): void {
  const { outbox, queryClient } = useDeps();
  useEffect(() => {
    let alive = true;
    const flush = async (force: boolean) => {
      const before = (await outbox.queued()).length;
      await outbox.flush({ force }).catch(() => undefined);
      const after = (await outbox.queued()).length;
      if (alive && after < before) await queryClient.invalidateQueries();
    };
    const onOnline = () => void flush(true);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void flush(false);
    };
    void flush(false);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    const t = setInterval(() => void flush(false), 30_000);
    // Ask the browser to keep what's queued even when storage runs short (spec 3A §13 risk 1).
    void navigator.storage
      ?.persisted?.()
      .then((already) => (already ? undefined : navigator.storage.persist?.()))
      .catch(() => undefined);
    return () => {
      alive = false;
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(t);
    };
  }, [outbox, queryClient]);
}
